import { Service } from '@angular/core';
import { Observable, of } from 'rxjs';

import type { OrganizationsRepository } from '../../domain/interfaces/organizations.interface';
import {
  ORG_HOME_FALLBACK_DEFAULT_MODE,
  ORG_HOME_FALLBACK_MODES,
  OrgContext,
  orgProductLine,
} from '../../domain/models/organization.model';
import { OrgWheel, OrgWheelAnswers, retainKnownAnswers } from '../../domain/models/org-wheel.model';
import {
  OrgChallenge,
  OrgEngagement,
  OrgEngagementOverview,
  OrgProgressOp,
  OrgProgressResult,
  OrgPulse,
} from '../../domain/models/org-engagement.model';
import {
  OrgHealthCatalog,
  OrgHealthSample,
  OrgHealthSyncResult,
} from '../../domain/models/org-health.model';
import {
  OrgBookingResult,
  OrgCounselingOptions,
  OrgCounselingSession,
  OrgGuardianDecisionResult,
  OrgGuardianRequest,
} from '../../domain/models/org-counseling.model';
import { OrgChild, OrgChildDigest, OrgConsentDecision, OrgConsentRequest } from '../../domain/models/org-guardian.model';

/**
 * OrganizationsMockRepository — designed offline payloads behind the same port,
 * for working on the B2B surfaces without the API (bound when
 * `ORGANIZATIONS_CONFIG.useMock` is true).
 *
 * The shapes are copied from the integration guide's own examples, INCLUDING its
 * awkward cases, because those are what the UI has to survive: a partial check-in
 * that amends rather than appends, the two-shaped counseling options, a sparse
 * digest map, and a challenge whose `can_report` is false. A mock that only ever
 * returned the happy payload would hide exactly the paths worth exercising.
 *
 * Copy is Arabic, as the server sends it (§1.5) — a translated mock would set the
 * wrong expectation about what the client is allowed to do with these strings.
 */
@Service()
export class OrganizationsMockRepository implements OrganizationsRepository {
  private wheel: OrgWheel = buildWheel();
  private engagement: OrgEngagement = buildEngagement();
  private sessions: readonly OrgCounselingSession[] = [buildSession()];

  getContext(): Observable<OrgContext> {
    return of({
      organization: {
        id: 3,
        kind: 'university' as const,
        name: 'جامعة القصيم',
        logoPath: 'organizations/qu-logo.png',
        logoUrl: null,
        badge: 'مُهدى من جامعة القصيم',
        privacyLine: 'بياناتك الفردية لا تُشارك مع جامعتك.',
      },
      membership: { memberCode: 'QU-00417', role: 'member' as const, unit: 'كلية الهندسة', stage: null },
      entitlement: { active: true, label: 'مُهدى من جامعة القصيم', endsAt: '2027-06-30T00:00:00+03:00', daysLeft: 291 },
      escalationTarget: 'وحدة الإرشاد الطلابي',
      // The SAME block the mapper falls back to for a member whose `/context`
      // carries no `home` — one constant, so the offline hub and that member's
      // hub can never drift apart.
      home: {
        productLine: orgProductLine('university', 'جامعة القصيم'),
        modes: ORG_HOME_FALLBACK_MODES,
        defaultMode: ORG_HOME_FALLBACK_DEFAULT_MODE,
      },
    });
  }

  getWheel(): Observable<OrgWheel> {
    return of(this.wheel);
  }

  /**
   * Mirrors the real amend semantics: the unique key is (user, day), so a second
   * submit updates the same check-in — and unknown keys are dropped SILENTLY,
   * which is the behaviour trap 7 exists to warn about.
   */
  submitCheckin(answers: OrgWheelAnswers): Observable<OrgWheel> {
    const kept = retainKnownAnswers(this.wheel, answers);
    const apply = (d: OrgWheel['dimensions'][number]) =>
      kept[d.key] != null ? { ...d, score: kept[d.key]! } : d;
    const core = this.wheel.core ? apply(this.wheel.core) : null;
    const dimensions = this.wheel.dimensions.map(apply);
    const scored = [core, ...dimensions].filter((d): d is NonNullable<typeof d> => d != null && d.score != null);
    this.wheel = {
      ...this.wheel,
      core,
      dimensions,
      answeredToday: Object.keys(kept).length,
      checkedInToday: Object.keys(kept).length > 0,
      composite: scored.length
        ? Math.round(scored.reduce((sum, d) => sum + (d.score ?? 0), 0) / scored.length)
        : null,
      crisis: (core?.score ?? 100) <= 25,
    };
    return of(this.wheel);
  }

  getEngagement(): Observable<OrgEngagement> {
    return of(this.engagement);
  }

  joinChallenge(challengeId: number): Observable<OrgEngagementOverview> {
    const challenges = this.engagement.challenges.map((c) =>
      c.id === challengeId ? { ...c, joined: true, canReport: c.selfReport } : c,
    );
    this.engagement = { ...this.engagement, challenges };
    // The PARTIAL shape, exactly as §4.3 documents it.
    return of({ unitId: 9, streakDays: this.engagement.streak.days, challenges });
  }

  reportProgress(
    challengeId: number,
    value: number,
    op: OrgProgressOp,
    _date?: string,
  ): Observable<OrgProgressResult> {
    let updated: OrgChallenge | null = null;
    const challenges = this.engagement.challenges.map((c) => {
      if (c.id !== challengeId) return c;
      const next = op === 'add' ? c.todayValue + value : value;
      const percent = c.target > 0 ? Math.min(100, Math.round((next / c.target) * 100)) : 0;
      updated = { ...c, todayValue: next, progress: next, percent };
      return updated;
    });
    this.engagement = { ...this.engagement, challenges };
    return of({
      value: updated ? (updated as OrgChallenge).todayValue : value,
      challenge: updated,
      overview: { unitId: 9, streakDays: this.engagement.streak.days, challenges },
    });
  }

  answerPulse(value: number): Observable<OrgPulse> {
    const question = this.engagement.pulse.question;
    const pulse: OrgPulse = {
      ...this.engagement.pulse,
      answered: true,
      question: question ? { ...question, myValue: value } : null,
    };
    this.engagement = { ...this.engagement, pulse };
    // The pulse BLOCK only — never the full engagement payload (trap 8).
    return of(pulse);
  }

  getHealthCatalog(): Observable<OrgHealthCatalog> {
    return of({
      metrics: [
        { key: 'steps', unit: 'خطوة', label: 'الخطوات' },
        { key: 'sleep_minutes', unit: 'دقيقة', label: 'دقائق النوم' },
        { key: 'mindful_minutes', unit: 'دقيقة', label: 'دقائق الصفاء الذهني' },
        { key: 'active_minutes', unit: 'دقيقة', label: 'دقائق النشاط' },
      ],
      sources: ['apple_health', 'google_fit', 'health_connect', 'seha', 'manual'],
      maxBackfillDays: 30,
      maxBatch: 200,
      privacy: 'تُستخدم هذه الأرقام لتتبّع تحدياتك فقط، ولا تُشارك مع جهة عملك أو جامعتك.',
    });
  }

  /** Partial acceptance, as designed — the first row lands, anything older is rejected. */
  syncHealthSamples(samples: readonly OrgHealthSample[]): Observable<OrgHealthSyncResult> {
    const accepted = samples.filter((s) => s.value >= 0 && s.date !== '');
    return of({
      accepted: accepted.length,
      rejected: samples
        .map((s, index) => ({ s, index }))
        .filter(({ s }) => s.value < 0 || s.date === '')
        .map(({ index }) => ({ index, reason: 'invalid_value' })),
      creditedMetrics: [...new Set(accepted.map((s) => s.metric))],
    });
  }

  getCounselingOptions(): Observable<OrgCounselingOptions> {
    return of({
      available: true,
      reason: null,
      message: null,
      counselors: [{ memberId: 55, name: 'أ. نورة العتيبي', title: 'المرشد الطلابي · مدارس الرياض' }],
      slots: [
        {
          slotId: 14,
          date: '2026-09-14',
          weekday: 'الأحد',
          timeLabel: 'الفسحة الأولى',
          startTime: '10:15',
          duration: 20,
          location: 'at_school',
          locationLabel: 'بالمدرسة',
          counselor: { memberId: 55, name: 'أ. نورة العتيبي', title: null },
        },
      ],
      guardian: { consentRequired: true, note: 'تُرسل موافقة لولي أمرك قبل تأكيد الموعد.' },
      cost: { free: true, label: 'بلا أي تكلفة عليك' },
      checklist: [
        'تأكد إنك بمكان هادئ (غرفة الإرشاد أو البيت).',
        'موافقة ولي أمرك على الجلسة.',
        'الجلسة سرّية — والتصعيد فقط عند وجود خطر.',
      ],
    });
  }

  bookCounseling(_slotId: number, date: string): Observable<OrgBookingResult> {
    this.sessions = [{ ...buildSession(), date }, ...this.sessions];
    return of({
      sessions: this.sessions,
      message: 'أرسلنا طلب الموافقة لولي أمرك — نأكّد الموعد أول ما يوافق.',
    });
  }

  getCounselingSessions(): Observable<readonly OrgCounselingSession[]> {
    return of(this.sessions);
  }

  cancelCounseling(sessionId: number, reason?: string | null): Observable<readonly OrgCounselingSession[]> {
    this.sessions = this.sessions.map((s) =>
      s.id === sessionId
        ? { ...s, status: 'cancelled', statusLabel: 'ملغي', cancelReason: reason?.trim() || null }
        : s,
    );
    return of(this.sessions);
  }

  getChildren(): Observable<readonly OrgChild[]> {
    return of([
      {
        memberId: 812,
        name: 'سارة',
        cohort: 'الصف الثالث/أ',
        stage: 'primary',
        relationship: 'father',
        tier: 'full',
        tierNote: 'رؤية كاملة مع موافقة مسبقة على كل خطوة، وإشعار فوري عند أي تصعيد.',
        sections: [
          'status',
          'escalation_alerts',
          'mood_trend',
          'program_progress',
          'appointments',
          'conversation_summary',
        ],
      },
    ]);
  }

  /** A SPARSE map: `available_sections` lists six, `sections` carries two (trap 15). */
  getChildDigest(memberId: number): Observable<OrgChildDigest> {
    return of({
      child: { memberId, name: 'سارة', cohort: 'الصف الثالث/أ', stage: 'primary', relationship: 'father' },
      tier: 'full',
      tierNote: 'رؤية كاملة مع موافقة مسبقة على كل خطوة، وإشعار فوري عند أي تصعيد.',
      sections: {
        status: { label: 'مستقرة', note: 'لا توجد تنبيهات هذا الأسبوع.' },
        mood_trend: { direction: 'up', points: [3, 3, 4, 4] },
      },
      availableSections: [
        'status',
        'escalation_alerts',
        'mood_trend',
        'program_progress',
        'appointments',
        'conversation_summary',
      ],
    });
  }

  getConsents(): Observable<readonly OrgConsentRequest[]> {
    return of([
      {
        referralId: 77,
        childName: 'سارة',
        caseType: 'academic_stress',
        priority: 'normal',
        askedAt: '2026-09-10T09:12:00+03:00',
        scope: ['status', 'escalation_alerts', 'mood_trend', 'program_progress'],
        note: 'ملخّص دوري للمزاج وتقدّم البرامج. محتوى المحادثات يظل خاصاً، وتُطلب موافقتك عند التصعيد.',
      },
    ]);
  }

  decideConsent(referralId: number, granted: boolean): Observable<OrgConsentDecision> {
    return of({ referralId, guardianState: granted ? 'consent_granted' : 'consent_declined' });
  }

  getGuardianRequests(): Observable<readonly OrgGuardianRequest[]> {
    return of([{ ...buildSession(), child: 'سارة' }]);
  }

  decideGuardianRequest(sessionId: number, approve: boolean): Observable<OrgGuardianDecisionResult> {
    const requests: readonly OrgGuardianRequest[] = [
      {
        ...buildSession(),
        id: sessionId,
        status: approve ? 'confirmed' : 'cancelled',
        statusLabel: approve ? 'مؤكّد' : 'ملغي',
        guardianState: approve ? 'consent_granted' : 'consent_declined',
        child: 'سارة',
      },
    ];
    return of({ requests, message: approve ? 'تم تأكيد الموعد.' : 'تم رفض الطلب.' });
  }
}

// ── designed payloads ────────────────────────────────────────────────────────

function buildWheel(): OrgWheel {
  const dim = (
    key: string,
    name: string,
    icon: string,
    hue: string,
    score: number | null,
  ): OrgWheel['dimensions'][number] => ({
    key,
    isCore: key === 'core',
    name,
    question: `كيف ${name} اليوم؟`,
    meaning: `وصف بُعد ${name}.`,
    icon,
    hue,
    score,
    band: score == null ? null : score >= 75 ? 'balanced' : score >= 50 ? 'acceptable' : 'needs_care',
    bandLabel: score == null ? null : score >= 75 ? 'متوازن' : score >= 50 ? 'مقبول' : 'يحتاج عناية',
    action: 'khoyek',
    actionLabel: 'تحدّث مع خويك',
    programmeKey: null,
    actionTarget: { type: null, id: null, title: null, exists: false },
  });

  return {
    core: dim('core', 'النفسي والعاطفي', 'heart', 'rose', 50),
    dimensions: [
      dim('bod', 'الجسدي', 'activity', 'emerald', null),
      dim('pro', 'الأكاديمي', 'briefcase', 'indigo', 75),
      dim('soc', 'الاجتماعي', 'users', 'sky', 100),
    ],
    composite: 62,
    answeredToday: 3,
    // Deliberately NOT 9 — trap 6: an org can show fewer dimensions.
    total: 4,
    checkedInToday: true,
    streak: 3,
    crisis: false,
    scale: [
      { value: 25, label: 'صعب' },
      { value: 50, label: 'متوسط' },
      { value: 75, label: 'جيد' },
      { value: 100, label: 'ممتاز' },
    ],
  };
}

function buildEngagement(): OrgEngagement {
  return {
    challenges: [
      {
        id: 41,
        key: 'water-september',
        title: 'تحدي الماء',
        tagline: '٢ لتر كل يوم',
        metric: 'water_liters',
        source: 'self_report',
        sourceLabel: 'إدخالك اليومي',
        selfReport: true,
        canReport: true,
        maxDailyValue: 6,
        target: 2,
        unitLabel: 'لتر',
        metricLabel: 'لترات الماء',
        cadence: 'daily',
        progress: 1.5,
        todayValue: 1.5,
        percent: 75,
        daysLeft: 12,
        joined: true,
        completed: false,
        participants: 214,
        myUnitRank: 2,
        leaderboard: [
          { unitId: 7, name: 'كلية الطب', value: 980.5, participants: 120, isMine: false, rank: 1 },
          { unitId: 9, name: 'كلية الهندسة', value: 812, participants: 94, isMine: true, rank: 2 },
        ],
        icon: 'droplet',
        hue: 'sky',
      },
      {
        // An auto-counted challenge: `can_report` false, so no manual input opens.
        id: 42,
        key: 'steps-september',
        title: 'تحدي المشي',
        tagline: '٨٠٠٠ خطوة',
        metric: 'steps',
        source: 'health',
        sourceLabel: 'تطبيق الصحة على جوالك',
        selfReport: false,
        canReport: false,
        maxDailyValue: null,
        target: 8000,
        unitLabel: 'خطوة',
        metricLabel: 'الخطوات',
        cadence: 'daily',
        progress: 6250,
        todayValue: 6250,
        percent: 78,
        daysLeft: 8,
        joined: true,
        completed: false,
        participants: 124,
        myUnitRank: 1,
        leaderboard: [{ unitId: 9, name: 'كلية الهندسة', value: 61200, participants: 94, isMine: true, rank: 1 }],
        icon: 'compass',
        hue: 'indigo',
      },
    ],
    streak: { days: 3, label: '3 أيام متتالية' },
    badges: [
      { key: 'achievement', label: 'الإنجاز', state: 'locked', progress: 0, target: 1, note: 'أكملت تحدياً كاملاً' },
      {
        key: 'balance',
        label: 'التوازن',
        state: 'in_progress',
        progress: 4,
        target: 9,
        note: 'أجبت على كل أبعاد عجلتك في يوم واحد',
      },
      {
        key: 'perseverance',
        label: 'المثابرة',
        state: 'in_progress',
        progress: 3,
        target: 7,
        note: 'سبعة أيام متتالية من الفحص اليومي',
      },
      {
        key: 'exploration',
        label: 'الاستكشاف',
        state: 'locked',
        progress: 0,
        target: 3,
        note: 'شاركت في ثلاثة تحديات مختلفة',
      },
    ],
    pulse: {
      period: '2026-W37',
      answered: false,
      question: {
        id: 8,
        text: 'كيف كان ضغط أسبوعك؟',
        options: [
          { value: 1, label: 'محتمل', hue: 'green' },
          { value: 2, label: 'متوسط', hue: 'amber' },
          { value: 3, label: 'مرتفع', hue: 'orange' },
          { value: 4, label: 'مرهق جداً', hue: 'red' },
        ],
        myValue: null,
        note: 'إجابتك تُجمع مع زملائك على مستوى وحدتك — لا تظهر باسمك.',
      },
    },
  };
}

function buildSession(): OrgCounselingSession {
  return {
    id: 90,
    date: '2026-09-14',
    timeLabel: 'الفسحة الأولى',
    startTime: '10:15',
    location: 'at_school',
    locationLabel: 'بالمدرسة',
    status: 'pending_guardian',
    statusLabel: 'بانتظار موافقة ولي الأمر',
    guardianState: 'pending',
    counselor: 'أ. نورة العتيبي',
    cancelReason: null,
  };
}
